import React, { useEffect } from 'react';
import { setPageTitle } from '../../utils/title';
import { trackEvent } from '../../utils/analytics';
import { useCart } from '../../context/CartContext';
import Breadcrumb from '../../components/Breadcrumb';
import './Solutions.scss';

const Infrastructure = () => {
  const { addToCart, removeFromCart, isInCart } = useCart();

  useEffect(() => {
    setPageTitle('Cloud Infrastructure for Systems and Applications');
  }, []);

  const handleCartAction = (solutionType, title) => {
    const itemId = `infrastructure-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    if (isItemInCart) {
      trackEvent('remove_from_cart', 'Infrastructure Solutions', {
        solution_name: 'Cloud Infrastructure',
        solution_type: solutionType,
        source_page: 'infrastructure_solutions',
      });
      removeFromCart(itemId);
    } else {
      trackEvent('add_to_cart', 'Infrastructure Solutions', {
        solution_name: 'Cloud Infrastructure',
        solution_type: solutionType,
        source_page: 'infrastructure_solutions',
      });
      addToCart({
        id: itemId,
        title,
        type: 'Infrastructure Solution',
      });
    }
  };

  const renderCartButton = (solutionType, title) => {
    const itemId = `infrastructure-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    return (
      <button
        className="card__link card__link--cart"
        onClick={() => handleCartAction(solutionType, title)}
      >
        {isItemInCart ? 'Remove from shopping cart' : 'Add to shopping cart'}
      </button>
    );
  };

  const breadcrumbs = [
    { path: '/', label: 'Home' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/solutions/infrastructure', label: 'Cloud Infrastructure' },
  ];

  return (
    <div className="solutions-page">
      <Breadcrumb items={breadcrumbs} />
      <section className="page-header section section--light">
        <div className="container">
          <p className="section__subtitle">AWS Infrastructure Management</p>
          <h1 className="page-header__title">Cloud Infrastructure for Systems and Apps</h1>
          <p className="page-header__description">
            We leverage the most suitable AWS services to host your applications, ensuring optimal
            cost, operational excellence, security, and operational efficiency.
          </p>
          <blockquote className="section__quote">
            All of our hosting solutions are implemented on auto-scaling clusters with load
            balancing and robust CI/CD pipelines featuring confident packaging, version labeling,
            smooth deployments and rollbacks, and with tailored operational monitoring based on AWS
            CloudWatch and/or Prometheus and Grafana.
          </blockquote>
        </div>
      </section>

      <section className="solutions-content section section--light">
        <div className="container">
          <div className="card solution-card">
            <h3 className="card__title">Containerized Apps Hosting on AWS ECS</h3>
            <p className="card__description">
              We expertly containerize your applications, establish and manage ECS clusters, set up
              task definitions, choose deployment types, define deployment and rollback thresholds,
              implement scaling strategies, implement cluster security, establish operational
              monitoring and alerting.
            </p>
            {renderCartButton('ecs_hosting', 'Containerized Apps Hosting on AWS ECS')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">Digital Platforms Hosting on AWS EKS</h3>
            <p className="card__description">
              We configure and run Kubernetes clusters tailored to your platform's needs. From
              implementing deployment strategies with autoscaling, ensuring secure networking, RBAC,
              to integrating advanced monitoring, we follow best practices for platform resilience
              and reliability.
            </p>
            {renderCartButton('eks_hosting', 'Digital Platforms Hosting on AWS EKS')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">Hosting of WordPress, e-Commerce Systems, and CRMs</h3>
            <p className="card__description">
              We provide expert hosting for your applications on Amazon EC2 through provisioning and
              managing EC2 instances, automating deployments with instance-specific scripts,
              implementing auto-scaling configurations, and ensuring compliance with security best
              practices.
            </p>
            {renderCartButton('ec2_hosting', 'Hosting of WordPress, e-Commerce Systems, and CRMs')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">Hosting of Serverless Workloads and Apps</h3>
            <p className="card__description">
              We host your workloads on AWS Lambda, API Gateway, and DynamoDB, implement
              event-driven architectures, optimize function execution, integrate CI/CD pipelines,
              and establish monitoring and alerting to meet your business needs while minimizing
              operational overhead.
            </p>
            {renderCartButton('serverless_hosting', 'Hosting of Serverless Workloads and Apps')}
          </div>

          <div className="card solution-card deprecated">
            <h3 className="card__title">Apps Hosting on Elastic BeansTalk *</h3>
            <p className="card__description">
              We simplify application hosting with AWS Elastic Beanstalk. Our services include
              configuring environments, optimizing application settings, integrating CI/CD
              pipelines, implementing security measures, monitoring performance, and fine-tuning
              resource allocation.
            </p>
            <p className="card__note--deprecated">* DEPRECATED</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Infrastructure;
